import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Ajax = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.ajax:About Ajax Paragraph 1">
            Ajax, an exceedingly livable town in{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText>, stands
            out for its eco-focused outlook and strong community feel. As one of
            the fastest-growing cities in the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area</LinkText>, Ajax
            boasts a thriving economy and diverse rental options, all within one
            of Canada’s safest areas. The town’s vibrancy is evident in its
            diverse population, with over 65 languages spoken—a testament to its
            multicultural fabric within Ontario.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.ajax:About Ajax Paragraph 2">
            Rental diversity thrives in Ajax, offering a wide selection of
            apartments, condos, and detached houses at reasonable rates.
            Positioned along Lake Ontario, waterfront access adds to the town’s
            appeal, while its various neighborhoods ensure a place for everyone
            in this up-and-coming community within the Greater Toronto Area.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,900 - $2,600',
          bed2: '$2,000 - $3,200',
          bed3: '$2,700 - $4,000'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.ajax:Apartment Hunting in Ajax Paragraph 1">
            Ajax is an attractive city of Ontario known for its thriving
            community and diverse housing options. While rental prices in Ajax
            are generally more affordable compared to Toronto, they have been
            steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>cheap rentals in Ajax</LinkText>,
            especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ajax:Apartments for rent Ajax Paragraph">
              While not as numerous as houses, there are several apartment
              complexes in Ajax, mainly catering to renters. These typically
              range from older, low-rise buildings to newer, mid-rise
              structures. Compared to cities like Toronto, renters can expect to
              see lower price tags for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Ajax
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent CITY Paragraph 2">
              There are plenty of{' '}
              <LinkText href={links.house}>
                houses for rent in {{ cityName }}
              </LinkText>{' '}
              for those with a higher budget, or who have multiple roommates. In
              more residential areas of the city, both partial houses and entire
              houses for rent are common – as well as both new builds and older
              homes from the city’s earlier days. {{ cityName }} is also known
              for an abundance of laneway homes and townhomes within the city,
              making it easy to find a place to rent that suits your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ajax:Townhouses for rent Ajax Paragraph">
              Townhouses are increasingly popular in Ajax since it combines the
              best qualities of both apartments and whole houses. Typically less
              expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in Ajax
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ajax:Condos for rent Ajax Paragraph">
              Due to a growing demand for diverse housing, condos are becoming
              increasingly common in Ajax.{' '}
              <LinkText href={links.apartment}>
                Condos for rent in Ajax
              </LinkText>{' '}
              are often located in key urban centers of the town, and are
              especially attractive to young professionals and downsizers.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ajax:Short term rentals Ajax Paragraph">
              Ajax has plenty of work opportunities, making it a popular place
              for those renting on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in Ajax
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months.
              The added flexibility is an appealing prospect for many renters,
              and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.ajax:Living in Ajax Paragraph">
          Living in Ajax blends community spirit, multicultural richness, and
          economic opportunities. As one of the fastest-growing cities in the
          Greater Toronto Area, Ajax offers diverse rental options and a booming
          economy. Safety, waterfront access, and eco-focus enhance the
          lifestyle. Proximity to{' '}
          <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>{' '}
          ensures urban convenience, while Ajax’s rapid growth promises an
          evolving and vibrant community. Experience a welcoming town that
          combines comfort, culture, and career prospects within one of the{' '}
          <LinkText href={neighborhoodsLink}>
            best places to rent near the Greater Toronto Area
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.ajax:Popular Neighbourhoods Ajax Paragraph">
          Like any city, Ajax is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Central Ajax, Southwood, Carruthers Creek, Eagle Ridge, Downtown Ajax,
          Lakeview, and Westney Heights. For more info about Ajax, you can
          consult this{' '}
          <LinkText href={neighborhoodsLink}>
            guide on Toronto neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.ajax:Local Landmarks Ajax Paragraph">
          Ajax, Ontario, nestled along the shores of Lake Ontario, boasts
          several landmarks that highlight its rich history and natural beauty.
          The Ajax Waterfront Park offers vast stretches of scenic coastline,
          providing residents and visitors with picturesque views and
          recreational spaces. The Veteran’s Point Gardens, established to
          commemorate the town’s World War II history, stands as a poignant
          reminder of the HMS Ajax, the town’s namesake. Further inland, the St.
          Francis Centre, a beautifully restored 19th-century church, now serves
          as a cultural hub for arts and performances. Additionally, Rotary
          Park, with its lush greenery and playgrounds, remains a favorite
          gathering spot for families and community events.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Ajax
